import { Card, CardContent, CardHeader, Stack, Tooltip } from '@mui/material';
import { FunctionComponent, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AccessibleButton } from '../../components/Accessibility';
import { SensorySensitivityCheckbox } from '../../components/admin/SensorySensitivityCheckbox';
import { DisplayCompatibilityWarning } from '../../components/DisplayCompatibilityWarning';
import { NarrowAdminLayout } from '../../components/layout/AdminLayout';
import { QuestionSetSelect } from '../../components/QuestionSetSelect';
import { TeamNameEntry } from '../../components/TeamNameEntry';
import { Analytics } from '../../data/Analytics';
import { FirestoreQuestionProvider } from '../../data/FirestoreQuestionProvider';
import { gameConfigStore, useGameConfigSelector } from '../../store/GameConfigStore';
import { gamePlayStore } from '../../store/GameplayStore';
import { Team } from '../../types/GameplayTypes';
import { isQuestionSetPlayable } from '../../util/GameConfigUtils';

export const GameSetupScreen: FunctionComponent = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const teamNames = useGameConfigSelector(gameConfigStore.selectors.teamNames);
	const questionSet = useGameConfigSelector(gameConfigStore.selectors.questionSet);

	const [nameA, setNameA] = useState(teamNames[0]);
	const [nameB, setNameB] = useState(teamNames[1]);
	const isPlayable = isQuestionSetPlayable(questionSet);

	async function startGame() {
		if (questionSet && isPlayable) {
			// Load questions into game state
			const provider = new FirestoreQuestionProvider();
			const questions = await provider.questions(questionSet.id);

			dispatch(gameConfigStore.actions.setTeamNames([nameA, nameB]));
			dispatch(gamePlayStore.actions.startGame(questions));

			Analytics.track('game_start', {
				question_set_id: questions.id, question_set_name: questions.name, question_count: questions.questions.length
			});

			void navigate('/welcome');
		}
	};

	return <NarrowAdminLayout title='Game Setup'>
		<Card>
			<CardHeader title='Teams' />
			<CardContent>
				<Stack spacing={2}>
					<TeamNameEntry team={Team.A} state={[nameA, setNameA]} />
					<TeamNameEntry team={Team.B} state={[nameB, setNameB]} />
				</Stack>
			</CardContent>
		</Card>

		<Card>
			<CardHeader title='Game' />
			<CardContent>
				<QuestionSetSelect />
				<SensorySensitivityCheckbox />
			</CardContent>
		</Card>

		<Tooltip title={isPlayable ? undefined : 'Select an active Question Set with questions'}>
			<Stack spacing={2}>
				<DisplayCompatibilityWarning />
				<AccessibleButton
					variant="contained"
					size="large"
					onClick={startGame}
					disabled={!nameA.trim() || !nameB.trim() || !isPlayable}
				>
					Start Game
				</AccessibleButton>
			</Stack>
		</Tooltip>
	</NarrowAdminLayout>;
};
